import type { Scene } from '@babylonjs/core/scene';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import '@babylonjs/core/Meshes/instancedMesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { CreateBox } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreateCapsule } from '@babylonjs/core/Meshes/Builders/capsuleBuilder';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import type { AssetId, GreyboxMaterialId, GreyboxPart, GreyboxRigNode } from '../../assets/AssetTypes';
import { arcPlacement } from '../../level/segments/segmentPath';
import { DEG_TO_RAD } from '../../utils/math';
import type { GreyboxMaterials } from '../GreyboxMaterials';

/**
 * Builds greybox placeholders from manifest parts.
 * Per asset, all parts sharing a material are merged into ONE source mesh;
 * every placed copy is a hardware instance → draw calls scale with the
 * number of (asset × material) combinations, not with the number of objects.
 *
 * Rigged greyboxes (a vehicle whose parts the game moves): the parts of each rig node are merged per material in
 * that node's space, every copy gets the node hierarchy with the rig's names (`<copy>:<node>`, like a GLB copy).
 */
interface GreyboxSource {
  readonly mesh: Mesh;
  /** Rig node the source belongs to, null = the root. */
  readonly node: string | null;
}

export class GreyboxFactory {
  private readonly sources = new Map<AssetId, GreyboxSource[]>();
  private readonly container: TransformNode;

  constructor(
    private readonly scene: Scene,
    private readonly materials: GreyboxMaterials,
  ) {
    this.container = new TransformNode('__GREYBOX_SOURCES__', scene);
  }

  /** Creates the shared source meshes up front (avoids hitches on first spawn). */
  prepare(id: AssetId, parts: readonly GreyboxPart[], rig: readonly GreyboxRigNode[] = []): void {
    if (!this.sources.has(id)) this.sources.set(id, this.buildSources(id, parts, rig));
  }

  instantiate(id: AssetId, parts: readonly GreyboxPart[], name: string, rig: readonly GreyboxRigNode[] = []): TransformNode {
    this.prepare(id, parts, rig);
    const root = new TransformNode(name, this.scene);
    const nodes = this.instantiateRig(root, rig, name);
    for (const source of this.sources.get(id) ?? []) {
      const instance = source.mesh.createInstance(`${name}:${source.mesh.name}`);
      instance.parent = (source.node ? nodes.get(source.node) : undefined) ?? root;
    }
    return root;
  }

  /** The rig's nodes under the copy's root, at their rest pose (local to their parents). */
  private instantiateRig(root: TransformNode, rig: readonly GreyboxRigNode[], name: string): Map<string, TransformNode> {
    const nodes = new Map<string, TransformNode>();
    const local = new Matrix();
    for (const spec of rig) {
      const node = new TransformNode(`${name}:${spec.name}`, this.scene);
      node.parent = (spec.parent ? nodes.get(spec.parent) : undefined) ?? root;
      // local = world × parent⁻¹ (Babylon multiplies row vectors: world = local × parent)
      const parent = spec.parent ? rig.find((n) => n.name === spec.parent) : undefined;
      rigMatrix(spec).multiplyToRef(parent ? rigMatrix(parent).invert() : Matrix.Identity(), local);
      const rotation = new Quaternion();
      local.decompose(undefined, rotation, node.position);
      node.rotationQuaternion = rotation;
      nodes.set(spec.name, node);
    }
    return nodes;
  }

  private buildSources(id: AssetId, parts: readonly GreyboxPart[], rig: readonly GreyboxRigNode[]): GreyboxSource[] {
    const groups = new Map<string, { node: string | null; material: GreyboxMaterialId; meshes: Mesh[] }>();
    parts.forEach((part, index) => {
      const mesh = this.buildPart(`${id}_part${index}`, part);
      const spec = part.node ? rig.find((n) => n.name === part.node) : undefined;
      if (part.node && !spec) throw new Error(`${id}: part ${index} references unknown rig node ${part.node}`);
      // Parts are given in asset space – a rig node's parts are moved into that node's space.
      if (spec) mesh.bakeTransformIntoVertices(rigMatrix(spec).invert());
      const key = `${spec?.name ?? ''}|${part.material}`;
      const group = groups.get(key) ?? { node: spec?.name ?? null, material: part.material, meshes: [] };
      group.meshes.push(mesh);
      groups.set(key, group);
    });

    const sources: GreyboxSource[] = [];
    for (const { node, material, meshes } of groups.values()) {
      const merged = meshes.length === 1 ? meshes[0] : Mesh.MergeMeshes(meshes, true, true);
      if (!merged) continue;
      merged.name = node ? `${node}__${material}` : `${id}__${material}`;
      merged.material = this.materials.get(material);
      merged.parent = this.container;
      // The source itself stays invisible; only its instances are rendered.
      merged.isVisible = false;
      merged.isPickable = false;
      merged.doNotSyncBoundingInfo = true;
      sources.push({ mesh: merged, node });
    }
    return sources;
  }

  private buildPart(name: string, part: GreyboxPart): Mesh {
    if (part.shape === 'arcBox') return buildArcBox(name, part, this.scene);
    const [w, h, d] = part.size;
    let mesh: Mesh;
    switch (part.shape) {
      case 'box':
        mesh = CreateBox(name, { width: w, height: h, depth: d }, this.scene);
        break;
      case 'cylinder':
        mesh = CreateCylinder(name, { diameter: w, height: h, tessellation: 12 }, this.scene);
        break;
      case 'sphere':
        mesh = CreateSphere(name, { diameter: w, segments: 8 }, this.scene);
        break;
      case 'capsule':
        mesh = CreateCapsule(name, { radius: w / 2, height: h, tessellation: 12 }, this.scene);
        break;
    }
    mesh.position.set(part.position[0], part.position[1], part.position[2]);
    if (part.rotation) {
      mesh.rotation.set(part.rotation[0] * DEG_TO_RAD, part.rotation[1] * DEG_TO_RAD, part.rotation[2] * DEG_TO_RAD);
    }
    mesh.bakeCurrentTransformIntoVertices();
    if (part.bandTile) applyBandUVs(mesh, part.bandTile);
    else applyMeterUVs(mesh);
    return mesh;
  }
}

/** Rest transform of a rig node in asset space (rotation in Babylon's yaw-pitch-roll order, like greybox parts). */
function rigMatrix(node: GreyboxRigNode): Matrix {
  const [rx, ry, rz] = (node.rotation ?? [0, 0, 0]).map((deg) => deg * DEG_TO_RAD) as [number, number, number];
  const [x, y, z] = node.position;
  return Matrix.Compose(Vector3.One(), Quaternion.RotationYawPitchRoll(ry, rx, rz), new Vector3(x, y, z));
}

/**
 * Belt UVs (GreyboxPart.bandTile): v = z / tile along the belt on every face, u across it (x on top and bottom, y on
 * the sides) – the contract of the GLB belts, so the game scrolls the texture the same way.
 */
function applyBandUVs(mesh: Mesh, tile: number): void {
  const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
  const normals = mesh.getVerticesData(VertexBuffer.NormalKind);
  if (!positions || !normals) return;
  const uvs = new Float32Array((positions.length / 3) * 2);
  for (let i = 0, v = 0; i < positions.length; i += 3, v += 2) {
    const sideways = Math.abs(normals[i] ?? 0) > Math.abs(normals[i + 1] ?? 0);
    uvs[v] = sideways ? (positions[i + 1] ?? 0) : (positions[i] ?? 0);
    uvs[v + 1] = (positions[i + 2] ?? 0) / tile;
  }
  mesh.setVerticesData(VertexBuffer.UVKind, uvs, false);
}

/** Angular step of swept arc boxes – smooth at greybox distances. */
const ARC_STEP_RADIANS = 2 * DEG_TO_RAD;

/**
 * Box bent along a curve module's centre line (part in path coordinates, see GreyboxArc).
 * Built directly as vertex data: one strip per face with outward normals and UVs in meters
 * (u along the path, v across/up), so tiled greybox textures continue without seams.
 */
function buildArcBox(name: string, part: GreyboxPart, scene: Scene): Mesh {
  const [width, height, length] = part.size;
  const [lateral, centerY, centerS] = part.position;
  const curvature = part.arc?.curvature ?? 0;
  const steps = Math.max(1, Math.ceil((Math.abs(curvature) * length) / ARC_STEP_RADIANS));
  const s0 = centerS - length / 2;
  const x0 = lateral - width / 2;
  const x1 = lateral + width / 2;
  const y0 = centerY - height / 2;
  const y1 = centerY + height / 2;

  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const at = { x: 0, y: 0, z: 0, yaw: 0 };
  const point = (x: number, y: number, s: number) => arcPlacement(curvature, x, y, s, at);

  /** Strip along the path: two edges (a, b) given as [lateral, y] per ring, normal from the ring's frame. */
  const strip = (a: readonly [number, number], b: readonly [number, number], normal: (yaw: number) => readonly [number, number, number], v: (edge: readonly [number, number]) => number) => {
    const base = positions.length / 3;
    for (let i = 0; i <= steps; i++) {
      const s = s0 + (length * i) / steps;
      for (const edge of [a, b]) {
        const p = point(edge[0], edge[1], s);
        positions.push(p.x, p.y, p.z);
        normals.push(...normal(p.yaw));
        uvs.push(s, v(edge));
      }
    }
    for (let i = 0; i < steps; i++) {
      const k = base + i * 2;
      addQuad(positions, indices, normal(point(lateral, centerY, s0 + (length * (i + 0.5)) / steps).yaw), k, k + 1, k + 3, k + 2);
    }
  };
  const up = () => [0, 1, 0] as const;
  const down = () => [0, -1, 0] as const;
  const right = (yaw: number) => [Math.cos(yaw), 0, -Math.sin(yaw)] as const;
  const left = (yaw: number) => [-Math.cos(yaw), 0, Math.sin(yaw)] as const;
  strip([x0, y1], [x1, y1], up, (e) => e[0]);
  strip([x0, y0], [x1, y0], down, (e) => e[0]);
  strip([x1, y0], [x1, y1], right, (e) => e[1]);
  strip([x0, y0], [x0, y1], left, (e) => e[1]);

  // End caps (normal = ∓ path tangent).
  for (const [s, sign] of [[s0, -1], [s0 + length, 1]] as const) {
    const base = positions.length / 3;
    const yaw = point(lateral, centerY, s).yaw;
    const normal = [sign * Math.sin(yaw), 0, sign * Math.cos(yaw)] as const;
    for (const [x, y] of [[x0, y0], [x1, y0], [x1, y1], [x0, y1]] as const) {
      const p = point(x, y, s);
      positions.push(p.x, p.y, p.z);
      normals.push(...normal);
      uvs.push(x, y);
    }
    addQuad(positions, indices, normal, base, base + 1, base + 2, base + 3);
  }

  const mesh = new Mesh(name, scene);
  const data = new VertexData();
  data.positions = positions;
  data.normals = normals;
  data.uvs = uvs;
  data.indices = indices;
  data.applyToMesh(mesh);
  return mesh;
}

/**
 * Two triangles a-b-c, a-c-d, wound so Babylon's face normal ((p1 − p2) × (p3 − p2),
 * left-handed) points along `outward` – i.e. they are front faces.
 */
function addQuad(positions: readonly number[], indices: number[], outward: readonly [number, number, number], a: number, b: number, c: number, d: number): void {
  const p = (i: number) => [positions[i * 3] ?? 0, positions[i * 3 + 1] ?? 0, positions[i * 3 + 2] ?? 0] as const;
  const [ax, ay, az] = p(a);
  const [bx, by, bz] = p(b);
  const [cx, cy, cz] = p(c);
  const u = [ax - bx, ay - by, az - bz];
  const v = [cx - bx, cy - by, cz - bz];
  const n = [
    (u[1] ?? 0) * (v[2] ?? 0) - (u[2] ?? 0) * (v[1] ?? 0),
    (u[2] ?? 0) * (v[0] ?? 0) - (u[0] ?? 0) * (v[2] ?? 0),
    (u[0] ?? 0) * (v[1] ?? 0) - (u[1] ?? 0) * (v[0] ?? 0),
  ];
  const facing = (n[0] ?? 0) * outward[0] + (n[1] ?? 0) * outward[1] + (n[2] ?? 0) * outward[2];
  if (facing >= 0) indices.push(a, b, c, a, c, d);
  else indices.push(a, c, b, a, d, c);
}

/**
 * Box-projects UVs in meters (world units) so tiled greybox textures have a
 * consistent real-world scale regardless of part size.
 */
function applyMeterUVs(mesh: Mesh): void {
  const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
  const normals = mesh.getVerticesData(VertexBuffer.NormalKind);
  if (!positions || !normals) return;
  const uvs = new Float32Array((positions.length / 3) * 2);
  for (let i = 0, v = 0; i < positions.length; i += 3, v += 2) {
    const nx = Math.abs(normals[i] ?? 0);
    const ny = Math.abs(normals[i + 1] ?? 0);
    const nz = Math.abs(normals[i + 2] ?? 0);
    const x = positions[i] ?? 0;
    const y = positions[i + 1] ?? 0;
    const z = positions[i + 2] ?? 0;
    if (ny >= nx && ny >= nz) {
      uvs[v] = x;
      uvs[v + 1] = z;
    } else if (nx >= nz) {
      uvs[v] = z;
      uvs[v + 1] = y;
    } else {
      uvs[v] = x;
      uvs[v + 1] = y;
    }
  }
  mesh.setVerticesData(VertexBuffer.UVKind, uvs, false);
}
